import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
    Box, Container, AppBar, Toolbar, Button,
    Menu, MenuItem, IconButton, Drawer, List, ListItem, ListItemText, 
    Divider, useMediaQuery, useTheme, ListItemIcon
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, TRANSLATED_ROUTES } from '../../i18n';
import routeMeta from '../../config/routeMeta.json';
import ApiIcon from '@mui/icons-material/Api';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExploreIcon from '@mui/icons-material/Explore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import MicIcon from '@mui/icons-material/Mic';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import Footer from '../common/Footer';
import Logo from '../common/Logo';
import { motion } from 'framer-motion';

const Layout = ({ children }) => {
    const { pathname } = useLocation();
    const meta = routeMeta.routes[pathname.replace(/(.)\/$/, '$1')] || routeMeta.default;
    // GitHub Pages serves each page as a folder (/quran/), so canonical URLs end in a slash.
    const pageUrl = `https://imuslimlife.com${pathname.endsWith('/') ? pathname : `${pathname}/`}`;
    const { t, i18n } = useTranslation();
    const lang = i18n.resolvedLanguage || 'en';
    const route = pathname.replace(/(.)\/$/, '$1');
    // Translated pages: one URL per language (?lang=xx), linked with hreflang.
    const translated = TRANSLATED_ROUTES.includes(route);
    const langUrl = (code) => (code === 'en' ? pageUrl : `${pageUrl}?lang=${code}`);
    const canonical = translated ? langUrl(lang) : pageUrl;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    // For the Prayer Times dropdown menu
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    
    // For mobile drawer
    const [drawerOpen, setDrawerOpen] = useState(false);

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    
    const handleDrawerToggle = () => {
        setDrawerOpen(!drawerOpen);
    };

    // Menu items
    const menuItems = [
        { 
            key: 'nav.prayerTimes', text: t('nav.prayerTimes'), 
            path: '/prayer-times',
            icon: <AccessTimeFilledIcon />,
            subItems: [
                { key: 'nav.calendar', text: t('nav.calendar'), path: '/calendar', icon: <CalendarTodayIcon /> },
                { key: 'nav.quran', text: t('nav.quran'), path: '/quran', icon: <MenuBookIcon /> },
                { key: 'nav.api', text: t('nav.api'), path: '/api', icon: <ApiIcon /> }
            ]
        },
        { key: 'nav.qibla', text: t('nav.qibla'), path: '/qibla', icon: <ExploreIcon /> },
        { key: 'nav.mobileApp', text: t('nav.mobileApp'), path: '/mobile-app', icon: <SmartphoneIcon /> },
        { key: 'nav.alexaSkill', text: t('nav.alexaSkill'), path: '/alexa-skill', icon: <MicIcon /> },
        { key: 'nav.niyyah', text: t('nav.niyyah'), path: '/niyyah', icon: <AutoAwesomeIcon /> },
        { key: 'nav.tasmi', text: t('nav.tasmi'), path: '/tasmi', icon: <RecordVoiceOverIcon /> }
    ];

    // Mobile drawer navigation
    const mobileDrawer = (
        <Drawer
            anchor={i18n.dir() === 'rtl' ? 'left' : 'right'}
            open={drawerOpen}
            onClose={handleDrawerToggle}
        >
            <Box
                sx={{ width: 250 }}
                role="presentation"
                component="nav"
            >
                <List>
                    {/* Animated Quran menu item at the top */}
                    <ListItem 
                        button 
                        component={Link} 
                        to="/quran"
                        onClick={handleDrawerToggle}
                    >
                        <ListItemIcon>
                            <motion.div
                                animate={{
                                    rotate: [0, 360, 360, 0],
                                    scale: [1, 1.3, 1.1, 1.4, 1],
                                    boxShadow: [
                                        '0 0 0px #fff',
                                        '0 0 16px #ffd700',
                                        '0 0 32px #00e5ff',
                                        '0 0 24px #ffd700',
                                        '0 0 0px #fff'
                                    ],
                                    y: [0, -6, 0, 6, 0]
                                }}
                                transition={{
                                    duration: 2.5,
                                    repeat: Infinity,
                                    repeatType: 'loop',
                                    ease: 'easeInOut'
                                }}
                                style={{ display: 'inline-flex' }}
                            >
                                <MenuBookIcon sx={{ color: '#e4c04d', filter: 'drop-shadow(0 0 8px #e4c04d) drop-shadow(0 0 16px #e4c04d)' }} />
                            </motion.div>
                        </ListItemIcon>
                        <ListItemText primary={t('nav.quran')} />
                    </ListItem>
                    <Divider />
                    {menuItems.map((item) => (
                        <React.Fragment key={item.key}>
                            <ListItem 
                                button 
                                component={Link} 
                                to={item.path}
                                onClick={handleDrawerToggle}
                            >
                                <ListItemIcon>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText primary={item.text} />
                            </ListItem>
                            
                            {item.subItems && item.subItems.map((subItem) => (
                                <ListItem 
                                    button 
                                    component={Link} 
                                    to={subItem.path}
                                    onClick={handleDrawerToggle}
                                    sx={{ pl: 4 }}
                                    key={subItem.key}
                                >
                                    <ListItemIcon>
                                        {subItem.icon}
                                    </ListItemIcon>
                                    <ListItemText primary={subItem.text} />
                                </ListItem>
                            ))}
                        </React.Fragment>
                    ))}
                </List>
            </Box>
        </Drawer>
    );

    // Basic Schema.org JSON-LD
    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "iMuslim Life",
        "url": "https://imuslimlife.com",
        "logo": "https://imuslimlife.com/logo512.png",
        "sameAs": [
            // Add social media links here if available
            // "https://www.facebook.com/yourpage",
            // "https://twitter.com/yourhandle",
            "https://github.com/imuslimlife/"
        ]
    };

    const websiteSchema = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "iMuslim Life",
        "url": "https://imuslimlife.com"
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Helmet>
                <title>{meta.title}</title>
                <meta name="description" content={meta.description} />
                {meta.index === false && <meta name="robots" content="noindex" />}
                <meta property="og:title" content={meta.title} />
                <meta property="og:description" content={meta.description} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonical} /> 
                <meta property="og:image" content="https://imuslimlife.com/logo512.png" /> 
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={meta.title} />
                <meta name="twitter:description" content={meta.description} />
                <meta name="twitter:image" content="https://imuslimlife.com/logo512.png" />
                {meta.index !== false && <link rel="canonical" href={canonical} />}
                {translated && meta.index !== false && LANGUAGES.map((l) => <link key={l.code} rel="alternate" hrefLang={l.code} href={langUrl(l.code)} />)}
                {translated && meta.index !== false && <link rel="alternate" hrefLang="x-default" href={pageUrl} />}
                {/* Add JSON-LD Schema */}
                <script type="application/ld+json">
                    {JSON.stringify(organizationSchema)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(websiteSchema)}
                </script>
            </Helmet>
            <AppBar component="header" position="static" sx={{
                background: 'linear-gradient(135deg, #006400, #004d00)'
            }}>
                <Container>
                    <Toolbar component="nav" disableGutters>
                        <Box 
                            component={Link} 
                            to="/" 
                            sx={{ 
                                textDecoration: 'none',
                                color: 'white',
                                flexGrow: 1,
                            }}
                        >
                            <Logo variant="default" size="medium" />
                        </Box>

                        {isMobile ? (
                            <IconButton
                                edge="end"
                                color="inherit"
                                aria-label={t('nav.menu')}
                                onClick={handleDrawerToggle}
                            >
                                <MenuIcon />
                            </IconButton>
                        ) : (
                            <Box component="nav" sx={{ display: 'flex' }}>
                                <Button
                                    component={Link}
                                    to="/quran"
                                    color="inherit"
                                    startIcon={
                                        <motion.div
                                            animate={{
                                                rotate: [0, 360, 360, 0],
                                                scale: [1, 1.3, 1.1, 1.4, 1],
                                                boxShadow: [
                                                    '0 0 0px #fff',
                                                    '0 0 16px #ffd700',
                                                    '0 0 32px #00e5ff',
                                                    '0 0 24px #ffd700',
                                                    '0 0 0px #fff'
                                                ],
                                                y: [0, -6, 0, 6, 0]
                                            }}
                                            transition={{
                                                duration: 2.5,
                                                repeat: Infinity,
                                                repeatType: 'loop',
                                                ease: 'easeInOut'
                                            }}
                                            style={{ display: 'inline-flex' }}
                                        >
                                            <MenuBookIcon sx={{ color: '#e4c04d', filter: 'drop-shadow(0 0 8px #e4c04d) drop-shadow(0 0 16px #e4c04d)' }} />
                                        </motion.div>
                                    }
                                    sx={{ marginInlineEnd: '8px', fontWeight: 700 }}
                                >
                                    {t('nav.quran')}
                                </Button>
                                <Button
                                    color="inherit"
                                    onClick={handleMenuClick}
                                    endIcon={<ArrowDropDownIcon />}
                                    sx={{ marginInlineEnd: '8px' }}
                                >
                                    {t('nav.prayerTimes')}
                                </Button>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleMenuClose}
                                    MenuListProps={{
                                        'aria-labelledby': 'prayer-times-button',
                                    }}
                                >
                                    <MenuItem 
                                        component={Link} 
                                        to="/prayer-times" 
                                        onClick={handleMenuClose}
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            color: theme.palette.text.primary
                                        }}
                                    >
                                        <AccessTimeFilledIcon fontSize="small" sx={{ marginInlineEnd: '8px' }} />
                                        {t('nav.prayerTimes')}
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem 
                                        component={Link} 
                                        to="/calendar" 
                                        onClick={handleMenuClose}
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            color: theme.palette.text.primary
                                        }}
                                    >
                                        <CalendarTodayIcon fontSize="small" sx={{ marginInlineEnd: '8px' }} />
                                        {t('nav.calendar')}
                                    </MenuItem>
                                    <MenuItem 
                                        component={Link} 
                                        to="/quran" 
                                        onClick={handleMenuClose}
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            color: theme.palette.text.primary
                                        }}
                                    >
                                        <motion.div
                                            animate={{
                                                rotate: [0, 360, 360, 0],
                                                scale: [1, 1.3, 1.1, 1.4, 1],
                                                boxShadow: [
                                                    '0 0 0px #fff',
                                                    '0 0 16px #e4c04d',
                                                    '0 0 32px #e4c04d',
                                                    '0 0 24px #e4c04d',
                                                    '0 0 0px #fff'
                                                ],
                                                y: [0, -6, 0, 6, 0]
                                            }}
                                            transition={{
                                                duration: 2.5,
                                                repeat: Infinity,
                                                repeatType: 'loop',
                                                ease: 'easeInOut'
                                            }}
                                            style={{ display: 'inline-flex' }}
                                        >
                                            <MenuBookIcon fontSize="small" sx={{ marginInlineEnd: '8px', color: '#e4c04d', filter: 'drop-shadow(0 0 8px #e4c04d) drop-shadow(0 0 16px #e4c04d)' }} />
                                        </motion.div>
                                        {t('nav.quran')}
                                    </MenuItem>
                                    <MenuItem 
                                        component={Link} 
                                        to="/api" 
                                        onClick={handleMenuClose}
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            color: theme.palette.text.primary
                                        }}
                                    >
                                        <ApiIcon fontSize="small" sx={{ marginInlineEnd: '8px' }} />
                                        {t('nav.api')}
                                    </MenuItem>
                                </Menu>

                                <Button
                                    component={Link}
                                    to="/qibla"
                                    color="inherit"
                                    sx={{ marginInlineEnd: '8px' }}
                                >
                                    {t('nav.qibla')}
                                </Button>

                                <Button
                                    component={Link}
                                    to="/mobile-app"
                                    color="inherit"
                                    sx={{ marginInlineEnd: '8px' }}
                                >
                                    {t('nav.mobileApp')}
                                </Button>
                                
                                <Button
                                    component={Link}
                                    to="/alexa-skill"
                                    color="inherit"
                                    sx={{ marginInlineEnd: '8px' }}
                                >
                                    {t('nav.alexaSkill')}
                                </Button>

                                <Button
                                    component={Link}
                                    to="/niyyah"
                                    color="inherit"
                                    sx={{ marginInlineEnd: '8px' }}
                                >
                                    {t('nav.niyyah')}
                                </Button>

                                <Button
                                    component={Link}
                                    to="/tasmi"
                                    color="inherit"
                                    sx={{ marginInlineEnd: '8px' }}
                                >
                                    {t('nav.tasmi')}
                                </Button>
                            </Box>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Mobile drawer */}
            {mobileDrawer}

            <Container component="main" sx={{ flexGrow: 1, py: 4 }}>
                {children}
            </Container>

            <Footer />
        </Box>
    );
};

export default Layout;